import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedSidebar from '../../components/ui/RoleBasedSidebar';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import MetricCard from './components/MetricCard';
import OrderCard from './components/OrderCard';
import InventoryAlert from './components/InventoryAlert';
import QuickActionButton from './components/QuickActionButton';
import StatusSummaryBar from './components/StatusSummaryBar';

const DashboardPrincipal = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole] = useState('admin');
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = String(now?.getHours())?.padStart(2, '0');
      const minutes = String(now?.getMinutes())?.padStart(2, '0');
      setCurrentTime(`${hours}:${minutes}`);
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  const metricsData = [
    {
      title: 'Ingresos del Día',
      value: '$45,230.50',
      icon: 'DollarSign',
      iconColor: 'var(--color-success)',
      trend: '+12.5% vs ayer',
      trendDirection: 'up',
      onClick: () => navigate('/caja')
    },
    {
      title: 'Órdenes Pendientes',
      value: '8',
      icon: 'ClipboardList',
      iconColor: 'var(--color-warning)',
      trend: '3 en proceso',
      trendDirection: 'up',
      onClick: () => navigate('/order-management')
    },
    {
      title: 'Servicios Completados',
      value: '24',
      icon: 'CheckCircle2',
      iconColor: 'var(--color-primary)',
      trend: '+8 desde ayer',
      trendDirection: 'up'
    },
    {
      title: 'Estado de Caja',
      value: 'Abierta',
      icon: 'Wallet',
      iconColor: 'var(--color-accent)',
      onClick: () => navigate('/caja')
    }
  ];

  const recentOrders = [
    {
      folio: '2026-0215-001',
      cliente: 'Juan Carlos Martínez',
      vehiculo: 'Toyota Corolla 2020 - ABC-1234',
      total: 3450.00,
      estado: 'En proceso',
      fecha: '15/02/2026'
    },
    {
      folio: '2026-0215-002',
      cliente: 'María Elena Rodríguez',
      vehiculo: 'Honda Civic 2019 - XYZ-5678',
      total: 5200.00,
      estado: 'Lista',
      fecha: '15/02/2026'
    },
    {
      folio: '2026-0214-045',
      cliente: 'Roberto Sánchez López',
      vehiculo: 'Nissan Sentra 2021 - DEF-9012',
      total: 2800.00,
      estado: 'Aprobada',
      fecha: '14/02/2026'
    },
    {
      folio: '2026-0214-044',
      cliente: 'Ana Patricia Gómez',
      vehiculo: 'Mazda 3 2018 - GHI-3456',
      total: 1950.00,
      estado: 'Cotizada',
      fecha: '14/02/2026'
    },
    {
      folio: '2026-0214-043',
      cliente: 'Luis Fernando Torres',
      vehiculo: 'Chevrolet Aveo 2017 - JKL-7890',
      total: 4100.00,
      estado: 'Entregada',
      fecha: '14/02/2026'
    }
  ];

  const inventoryAlerts = [
    {
      producto: 'Llanta 205/55R16 Michelin',
      stockActual: 0,
      stockMinimo: 8,
      categoria: 'Llantas - Sedán'
    },
    {
      producto: 'Aceite Sintético 5W-30',
      stockActual: 3,
      stockMinimo: 15,
      categoria: 'Lubricantes'
    },
    {
      producto: 'Filtro de Aire Universal',
      stockActual: 5,
      stockMinimo: 12,
      categoria: 'Filtros'
    },
    {
      producto: 'Llanta 185/65R15 Goodyear',
      stockActual: 2,
      stockMinimo: 10,
      categoria: 'Llantas - Compacto'
    },
    {
      producto: 'Batería 12V 60Ah',
      stockActual: 4,
      stockMinimo: 8,
      categoria: 'Baterías'
    }
  ];

  const getQuickActions = () => {
    const actions = {
      admin: [
        {
          label: 'Gestión de Precios',
          icon: 'DollarSign',
          iconColor: 'var(--color-success)',
          description: 'Actualizar precios y descuentos de servicios y productos',
          onClick: () => navigate('/productos')
        },
        {
          label: 'Reportes Completos',
          icon: 'BarChart3',
          iconColor: 'var(--color-primary)',
          description: 'Ver análisis detallado de ventas e inventario',
          onClick: () => navigate('/reportes')
        },
        {
          label: 'Gestión de Usuarios',
          icon: 'UserCog',
          iconColor: 'var(--color-accent)',
          description: 'Administrar roles y permisos del personal',
          onClick: () => navigate('/usuarios')
        }
      ],
      caja: [
        {
          label: 'Procesar Pago',
          icon: 'CreditCard',
          iconColor: 'var(--color-success)',
          description: 'Registrar pagos parciales o completos',
          onClick: () => navigate('/caja')
        },
        {
          label: 'Cierre de Caja',
          icon: 'Calculator',
          iconColor: 'var(--color-primary)',
          description: 'Generar reporte de cierre diario',
          onClick: () => navigate('/caja')
        },
        {
          label: 'Imprimir Tickets',
          icon: 'Printer',
          iconColor: 'var(--color-accent)',
          description: 'Reimprimir recibos de pago',
          onClick: () => navigate('/caja')
        }
      ],
      ventas: [
        {
          label: 'Nueva Cotización',
          icon: 'FileText',
          iconColor: 'var(--color-primary)',
          description: 'Crear cotización para cliente',
          onClick: () => navigate('/order-management')
        },
        {
          label: 'Gestión de Clientes',
          icon: 'Users',
          iconColor: 'var(--color-accent)',
          description: 'Administrar información de clientes y vehículos',
          onClick: () => navigate('/clientes')
        },
        {
          label: 'Consultar Inventario',
          icon: 'Package',
          iconColor: 'var(--color-success)',
          description: 'Ver disponibilidad de productos',
          onClick: () => navigate('/inventario')
        }
      ],
      tecnico: [
        {
          label: 'Órdenes Asignadas',
          icon: 'Wrench',
          iconColor: 'var(--color-primary)',
          description: 'Ver trabajos pendientes y en proceso',
          onClick: () => navigate('/order-management')
        },
        {
          label: 'Actualizar Estado',
          icon: 'RefreshCw',
          iconColor: 'var(--color-accent)',
          description: 'Cambiar estado de órdenes de trabajo',
          onClick: () => navigate('/order-management')
        },
        {
          label: 'Agregar Diagnóstico',
          icon: 'ClipboardCheck',
          iconColor: 'var(--color-success)',
          description: 'Registrar hallazgos y recomendaciones',
          onClick: () => navigate('/order-management')
        }
      ]
    };

    return actions?.[userRole] || actions?.admin;
  };

  const handleOrderClick = (folio) => {
    navigate('/order-management', { state: { searchFolio: folio } });
  };

  const handleInventoryAlertClick = (producto) => {
    navigate('/inventario', { state: { searchProduct: producto } });
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedSidebar 
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        userRole={userRole}
      />
      <main 
        className={`
          transition-smooth min-h-screen
          ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}
        `}
      >
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <NavigationBreadcrumb />

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Dashboard Principal
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Resumen de operaciones del día - {new Date()?.toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            <StatusSummaryBar
              cajaAbierta={true}
              turno="Matutino"
              cajero="Usuario Admin"
              montoInicial={5000.00}
              currentTime={currentTime}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {metricsData?.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                icon={metric?.icon}
                iconColor={metric?.iconColor}
                trend={metric?.trend}
                trendDirection={metric?.trendDirection}
                onClick={metric?.onClick}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Órdenes Recientes
                  </h2>
                  <button
                    onClick={() => navigate('/order-management')}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors focus-ring rounded px-2 py-1"
                  >
                    Ver todas
                  </button>
                </div>
                <div className="space-y-3 md:space-y-4">
                  {recentOrders?.map((order) => (
                    <OrderCard
                      key={order?.folio}
                      folio={order?.folio}
                      cliente={order?.cliente}
                      vehiculo={order?.vehiculo}
                      total={order?.total}
                      estado={order?.estado}
                      fecha={order?.fecha}
                      onClick={() => handleOrderClick(order?.folio)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1 mb-6 md:mb-8">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Alertas de Inventario
                  </h2>
                  <button
                    onClick={() => navigate('/inventario')}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors focus-ring rounded px-2 py-1"
                  >
                    Ver todo
                  </button>
                </div>
                <div className="space-y-3">
                  {inventoryAlerts?.map((alert, index) => (
                    <InventoryAlert
                      key={index}
                      producto={alert?.producto}
                      stockActual={alert?.stockActual}
                      stockMinimo={alert?.stockMinimo}
                      categoria={alert?.categoria}
                      onClick={() => handleInventoryAlertClick(alert?.producto)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1">
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4 md:mb-6">
              Acciones Rápidas
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getQuickActions()?.map((action, index) => (
                <QuickActionButton
                  key={index}
                  label={action?.label}
                  icon={action?.icon}
                  iconColor={action?.iconColor}
                  description={action?.description}
                  onClick={action?.onClick}
                />
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPrincipal;